import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import * as Buttons from 'theme/components/Buttons.style';
import * as Text from 'theme/components/Text.style';
import { SelectionDropdown } from 'shared/components/dropdown/SelectionDropdown';
import * as Section from 'settings/styles/SettingsSection.style';
import * as Styled from 'settings/styles/Account.style';
import * as PillField from 'settings/styles/PillField.style';
import {
  SUPPORTED_LANGUAGES,
  SupportedLanguage,
  LANGUAGE_NATIVE_NAMES,
} from 'i18n/languages';

interface AccountProps {
  email?: string;
  nickname: string;
  onSaveNickname: (nickname: string) => void | Promise<void>;
  language: SupportedLanguage;
  officialLanguage?: string;
  onChangeLanguage: (value: SupportedLanguage) => void | Promise<void>;
  onSignOutAllDevices: () => void;
}

export default function Account({
  email,
  nickname,
  onSaveNickname,
  language,
  officialLanguage,
  onChangeLanguage,
  onSignOutAllDevices,
}: AccountProps): JSX.Element {
  const { t } = useTranslation('pageSettings');
  const [isEditing, setIsEditing] = useState(false);
  const [draft, handleDraft] = useState(nickname);

  const canSave = useMemo(() => {
    const trimmed = draft.trim();
    return trimmed.length > 0 && trimmed !== nickname;
  }, [draft, nickname]);

  const handleEdit = () => {
    handleDraft(nickname);
    setIsEditing(true);
  };

  const handleCancel = () => {
    handleDraft(nickname);
    setIsEditing(false);
  };

  const handleSave = async () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    await onSaveNickname(trimmed);
    setIsEditing(false);
  };

  return (
    <>
      <Section.SectionHeader $first>
        <Text.H5>{t('account.title')}</Text.H5>
      </Section.SectionHeader>

      <Section.SectionCard>
        <Section.Rows>
          <Section.RowListItem>
            <Section.RowLabel>
              <Text.P2Semibold>{t('account.emailLabel')}</Text.P2Semibold>
              <Styled.EmailValue>{email}</Styled.EmailValue>
            </Section.RowLabel>
          </Section.RowListItem>

          <Section.Divider />

          <Section.RowListItem>
            {!isEditing ? (
              <>
                <Section.RowLabel>
                  <Text.P2Semibold>
                    {t('account.nicknameLabel')}
                  </Text.P2Semibold>
                  <Text.P2>{nickname}</Text.P2>
                </Section.RowLabel>

                <Section.RowControl>
                  <Buttons.SecondaryLight small onClick={handleEdit}>
                    {t('account.edit')}
                  </Buttons.SecondaryLight>
                </Section.RowControl>
              </>
            ) : (
              <Styled.EditBlock>
                <Text.P2Semibold>{t('account.nicknameLabel')}</Text.P2Semibold>

                <Styled.EditControls>
                  <Styled.EditFieldGrow>
                    <PillField.PillTextField
                      fullWidth
                      value={draft}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleDraft(e.target.value)
                      }
                      placeholder={t('account.nicknameLabel')}
                    />
                  </Styled.EditFieldGrow>

                  <Styled.EditButtonsRow>
                    <Buttons.SecondaryLight medium onClick={handleCancel}>
                      {t('account.cancel')}
                    </Buttons.SecondaryLight>

                    <Buttons.Primary
                      medium
                      onClick={handleSave}
                      disabled={!canSave}
                    >
                      {t('account.save')}
                    </Buttons.Primary>
                  </Styled.EditButtonsRow>
                </Styled.EditControls>
              </Styled.EditBlock>
            )}
          </Section.RowListItem>

          <Section.Divider />

          <Section.RowListItem $mobileColumn>
            <Section.RowLabel>
              <Text.P2Semibold>{t('language.selectLanguage')}</Text.P2Semibold>
            </Section.RowLabel>
            <Section.RowControl>
              <SelectionDropdown
                ariaLabel={t('language.selectLanguage')}
                value={language}
                options={SUPPORTED_LANGUAGES.map((code) => ({
                  value: code,
                  label: LANGUAGE_NATIVE_NAMES[code],
                  description:
                    code === officialLanguage
                      ? t('language.officialLanguage')
                      : undefined,
                }))}
                onChange={(v) => onChangeLanguage(v as SupportedLanguage)}
              />
            </Section.RowControl>
          </Section.RowListItem>

          <Section.Divider />

          <Section.RowListItem>
            <Section.RowLabel>
              <Text.P2Semibold>
                {t('account.signOutAllDevices')}
              </Text.P2Semibold>
            </Section.RowLabel>
            <Section.RowControl>
              <Buttons.SecondaryLight small onClick={onSignOutAllDevices}>
                {t('account.signOut')}
              </Buttons.SecondaryLight>
            </Section.RowControl>
          </Section.RowListItem>
        </Section.Rows>
      </Section.SectionCard>
    </>
  );
}
